import { useEffect, useMemo, useState } from "react";
import { ResourceType, SearchFilterOptionType, ResourceBookingSchema, PreferenceSchema} from "../../data/types";
// import KitchensPage from "../shared/KitchensPage";
import ResourceList from '../../molecules/ResourceList'
import FilterBox from '../../components/FilterBox'
import { capitalizeString } from "../../utils/helpers";
import { getOfferedResourcePageOptions } from "../../utils/helpers/dashboardStats";
import { useTeamAccess } from "../../hooks/communityhook";
import { canAccess } from "../../utils/teamPermissions";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AllResourceRequests } from "./RequestsPage";
import { useLoadCommunityBookings } from "../../hooks/memberhooks";
import Calendar from "../../molecules/Calendar";
import { useLoadCommunity } from "../../hooks/authhooks";
import { useAuthState } from "../../contexts/authcontext";
import { AddReservationModal } from "../../molecules/AppModal";
import { ReactComponent as ResourcesIcon } from "../../icons/ResourcesIcon.svg";
// import ToggleSwitch from "../../components/ToggleSwitch";



type RouteParams = {
  resourceType: ResourceType
};


type ResourcesPageProps = {
  searchFilterOptions: SearchFilterOptionType
}

const ResourcesPage: React.FC<ResourcesPageProps> = ({searchFilterOptions}) => {

  const navigate = useNavigate();
  var { resourceType } = useParams<RouteParams>();
  resourceType = resourceType ? resourceType : "kitchen"

  var { authUser } = useAuthState()
  var communityId = (authUser && authUser.primary_community) ? authUser.primary_community : ""
  var { primaryCommunity } = useLoadCommunity(communityId)
  const communityPreferences = primaryCommunity?.preferences
  // const defaultResourceSelected: PreferenceSchema = {
  //   kitchen: false,
  //   documentation: false,
  //   training: false,
  //   funding: false,
  //   expert: false,
  //   farm: false,
  // }
  const resourceSelected = communityPreferences as PreferenceSchema 
  const resourceOptions = useMemo(
    () => getOfferedResourcePageOptions(resourceSelected),
    [resourceSelected]
  )
  const currentOption = resourceOptions.find((opt) => opt.type === resourceType) || resourceOptions[0]

  useEffect(() => {
    document.title = "HomeCook | Resources";
  }, []);

  useEffect(() => {
    if (resourceOptions.length === 0) {
      return
    }
    if (!resourceOptions.some((option) => option.type === resourceType)) {
      navigate(`/resources/${resourceOptions[0].type}`, { replace: true })
    }
  }, [navigate, resourceOptions, resourceType]);

  return  (
    <div className="page-canvas h-auto">
      {resourceOptions.length === 0 ? (
        <div className="flex flex-col w-11/12 mx-auto my-8 rounded-2xl border border-[#E9E9E9] bg-white">
          <div className="flex flex-col items-center justify-center px-6 py-16">
            <div className="flex h-[80px] w-[80px] items-center justify-center rounded-full bg-[#EFF8FA]">
              <ResourcesIcon className="h-[40px] w-[40px] text-primary-400" />
            </div>
            <p className="mt-4 text-center font-raleway text-[20px] font-bold text-primary-200">
              Choose resources to offer
            </p>
            <p className="mt-2 max-w-md text-center font-open-sans text-[16px] text-[#333333]">
              Select the resource types you offer in Settings to start adding listings.
            </p>
            <button
              onClick={() => navigate("/settings")}
              className="mt-5 rounded-md border-0 bg-primary-200 px-6 py-3 font-raleway text-[16px] font-medium text-white"
            >
              Open Settings
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-row bg-white h-[75px] w-full">
            {resourceOptions.map((option, i) => (
              <div
                key={i}
                onClick={() => navigate(`/resources/${option.type}`)}
                className={`${(currentOption?.name === option.name) ? 'text-white bg-primary-200 font-medium' : 'text-[#4D4D4D] '} ml-6 my-auto text-[16px] font-raleway border-[1px] border-[#E9E9E9] py-2 px-4 rounded-md cursor-pointer`}
              >
                {option.name}
              </div>
            ))}
          </div>
          {currentOption ? (
            <ResourceTypeView resourceType={currentOption.type} searchFilterOptions={searchFilterOptions} />
          ) : null}
        </>
      )}
    </div>
  );
};


type ResourceTypeViewProps = {
  resourceType: ResourceType
  searchFilterOptions: SearchFilterOptionType
}

type ResourceMenuOption = "overview" | "bookings" | "calendar"

const getInitialResourceMenuOption = (
  view: string | null,
  resourceType: ResourceType
): ResourceMenuOption => {
  if (view === "bookings") {
    return "bookings";
  }

  if (view === "calendar" && ["kitchen", "training", "expert"].includes(resourceType)) {
    return "calendar";
  }

  return "overview";
};

export const ResourceTypeView: React.FC<ResourceTypeViewProps> = ({resourceType, searchFilterOptions}) => {

  const [searchParams] = useSearchParams();
  const { bookings } = useLoadCommunityBookings(resourceType);
  const { permissions } = useTeamAccess();
  const canManageResources = canAccess(permissions, "manageResources");
  const canManageRequests = canAccess(permissions, "manageRequests");

  type menuOptionTypes = ResourceMenuOption
  const visibleMenuOptions = useMemo(() => {
    const options: {id: menuOptionTypes, name: string}[] = [
      {id: "overview", name: "Overview"},
      {id: "bookings", name: `${resourceType} Requests (${bookings && bookings.length > 0 ? bookings.length : 0})`}
    ]
    if (["kitchen", "training", "expert"].includes(resourceType)) {
      options.push({id: "calendar", name: `Calendar`})
    }
    return options.filter((option) =>
      option.id === "overview" ? canManageResources : canManageRequests
    )
  }, [bookings, canManageRequests, canManageResources, resourceType])
  const [currentOption, setCurrentOption] = useState<menuOptionTypes>(() =>
    getInitialResourceMenuOption(searchParams.get("view"), resourceType)
  );
  const [isAddingResource, setIsAddingResource] = useState<boolean>(
    () => searchParams.get("create") === "1"
  )
  const [isAddingBooking, setIsAddingBooking] = useState<boolean>(false)
  const [resourceId, setResourceId] = useState<string>("")

  const handleOpenAddBooking = (resourceId: string) => {
    setResourceId(resourceId)
    setIsAddingBooking(true)
  }
  // const [calendarView, toggleCalendarView] = useState(false);

  useEffect(() => {
    setCurrentOption(getInitialResourceMenuOption(searchParams.get("view"), resourceType));
  }, [searchParams, resourceType]);

  useEffect(() => {
    if (visibleMenuOptions.length > 0 && !visibleMenuOptions.some((option) => option.id === currentOption)) {
      setCurrentOption(visibleMenuOptions[0].id);
    }
  }, [visibleMenuOptions, currentOption]);

  if (currentOption === 'calendar' && !["kitchen", "training", "expert"].includes(resourceType)) {
    setCurrentOption("bookings")
  }



  return (
    <>
      <div className="flex flex-row bg-white h-auto w-full my-1">
        {
          visibleMenuOptions.map((option, i)=> (
          <div 
            key={i} 
            className={`${(currentOption === option.id) ? 'text-primary-200 font-bold' : 'text-[#4D4D4D]'}  ml-6 mt-auto my-auto text-[14px] font-raleway py-2 px-4 rounded-md cursor-pointer`}
            onClick={()=> setCurrentOption(option.id)}
          >
            {capitalizeString(option.name)}
          </div>
          ))
        }
      </div>

      {
        (currentOption === 'bookings') ? (
          <>
            <AllResourceRequests resourceType={resourceType} resourceRequests={bookings || []} />
          </>
        ) : (
          (currentOption === 'overview') ? (
            <>
              <FilterBox searchType={resourceType} searchFilterOptions={searchFilterOptions} setIsAddingResource={setIsAddingResource} />
              <div className="flex flex-col w-11/12 mx-auto my-4">
                <ResourceList resourceType={resourceType} isAddingResource={isAddingResource} searchFilterOptions={searchFilterOptions} setIsAddingResource={setIsAddingResource} />
              </div>
            </>
          ) : ( 
            (currentOption === 'calendar' && ["kitchen", "training", "expert"].includes(resourceType)) ? (
              <div className="flex flex-col w-11/12 h-auto bg-white mx-auto my-6 rounded-xl shadow-md">
                {/* <div className="flex flex-row justify-end">
                  <ToggleSwitch
                    onChange={() => toggleCalendarView(!calendarView)}
                    isChecked={calendarView}
                  />
                </div> */}
                <Calendar bookings={bookings as ResourceBookingSchema[]} handleOpenAddBooking={handleOpenAddBooking} />
              </div>
            ) : ( <></>)
          )
        )
      }

      {isAddingBooking && (
        <AddReservationModal resourceId={resourceId} isOpen={isAddingBooking} closeAppModal={() => setIsAddingBooking(false)} bookings={bookings as ResourceBookingSchema[]} />
      )}
    </>
  )
}

export default ResourcesPage;
